import React, { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GUESTHOUSE_API_BASE = `${API_ROOT}/api/guesthouse`;

const nw = { whiteSpace: "nowrap" };
const right = { ...nw, textAlign: "right" };
const ellipsisCol = (width) => ({
  width,
  maxWidth: width,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "left",
});
const transIdCol = ellipsisCol(120);
const visitorNameCol = ellipsisCol(200);
const addressCol = { textAlign: "left" };
const addressInner = {
  width: 300,
  maxWidth: 300,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
};
const linkBtn = {
  cursor: "pointer",
  textDecoration: "underline",
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
};

const GH_PREFIX = { 1: "GH", 2: "GJ", 3: "FH", 4: "SH", 5: "DH" };
const PM_LABELS  = { O: "Online", C: "Cash", Q: "Cheque", P: "Pending", F: "Free Charge" };

function firstOfMonth() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(v, dash = false) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return dash ? `${dd}-${mm}-${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

function fmtDateTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function fmtTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function calcDays(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "";
  const d1 = new Date(String(checkIn).slice(0, 10));
  const d2 = new Date(String(checkOut).slice(0, 10));
  const diff = Math.round((d2 - d1) / 86400000);
  return diff >= 0 ? String(diff) : "";
}

function gstBreakdown(row) {
  const ghId = parseInt(row.GuestHouseID, 10) || 0;
  let gstAmount;
  if (row.GSTAmount != null) {
    gstAmount = parseFloat(row.GSTAmount);
  } else if (row.GSTPercent != null && row.Amount != null) {
    const misc = parseFloat(row.MiscAmount || 0);
    const discount = parseFloat(row.Discount || 0);
    gstAmount = (parseFloat(row.Amount) + misc - discount) * parseFloat(row.GSTPercent) / 100;
  } else {
    gstAmount = 0;
  }
  const half = gstAmount / 2;
  if ([1, 2, 3].includes(ghId)) return { cgst: half, utgst: half, sgst: 0 };
  if ([4, 5].includes(ghId))    return { cgst: half, utgst: 0,    sgst: half };
  return { cgst: 0, utgst: 0, sgst: 0 };
}

function fmtN(n) {
  return n === 0 ? "0.00" : n.toFixed(2);
}

function billNo(row) {
  if (row.SerialNumber == null || row.SerialNumber === "") return "";
  const prefix = GH_PREFIX[parseInt(row.GuestHouseID, 10)] || "";
  return `${prefix}${String(row.SerialNumber).padStart(4, "0")}`;
}

export default function GuesthouseInvoiceDetails() {
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);

  const [guesthouses, setGuesthouses]   = useState([]);
  const [guestHouseID, setGuestHouseID] = useState("ALL");
  const [fromDate, setFromDate]         = useState(firstOfMonth());
  const [toDate, setToDate]             = useState(todayStr());
  const [viewType, setViewType]         = useState("invoices"); // "all" | "invoices" | "cancelled"
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [searched, setSearched]         = useState(false);

  const showCancelCols = viewType !== "invoices";

  useEffect(() => {
    if (![10, 25].includes(moduleId)) return;
    fetch(`${GUESTHOUSE_API_BASE}/booking/guesthouses?moduleId=${moduleId}`)
      .then((r) => r.json())
      .then((d) => setGuesthouses(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [moduleId]);

  if (![10, 25].includes(moduleId)) {
    return (
      <section className="container-fluid">
        <div className="alert alert-danger mt-3">Unauthorized access Or Session Timed out.</div>
      </section>
    );
  }

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError("Please select both From Date and To Date."); return; }
    try {
      setLoading(true); setError(""); setRows([]);
      const q = new URLSearchParams({ moduleId: String(moduleId), guestHouseID, fromDate, toDate, view: viewType });
      const res = await fetch(`${GUESTHOUSE_API_BASE}/invoice-details?${q}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch");
      setRows(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch (err) {
      setError(err.message || "Failed to fetch invoice details");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!fromDate || !toDate) { setError("Please select both From Date and To Date."); return; }
    setError("");
    const q = new URLSearchParams({ moduleId: String(moduleId), guestHouseID, fromDate, toDate, view: viewType });
    window.location.href = `${GUESTHOUSE_API_BASE}/invoice-details/export?${q}`;
  };

  const openInvoice = (bookingId, invoiceId) => {
    if (!bookingId) return;
    const q = new URLSearchParams({ moduleId: String(moduleId) });
    if (invoiceId) q.set("invoiceId", String(invoiceId));
    window.open(
      `${GUESTHOUSE_API_BASE}/booking/invoice-pdf/${encodeURIComponent(bookingId)}?${q}`,
      "_blank", "noopener,noreferrer"
    );
  };

  const openEdit = (bookingId) => {
    window.open(
      `/guesthouse/admin-edit/${encodeURIComponent(bookingId)}?moduleId=${moduleId}`,
      "editBooking",
      "width=1000,height=600,scrollbars=yes,resizable=yes"
    );
  };

  // Compute running totals
  let sumRent = 0, sumMisc = 0, sumCGST = 0, sumUTGST = 0, sumSGST = 0, sumTotal = 0;
  const processed = rows.map((row) => {
    const discount  = parseFloat(row.Discount   || 0);
    const rawAmount = row.Amount != null ? parseFloat(row.Amount) : null;
    const rent      = rawAmount != null ? rawAmount - discount : null;
    const misc      = parseFloat(row.MiscAmount || 0);
    const net       = parseFloat(row.NetAmount  || 0);
    const gst       = gstBreakdown(row);

    if (rent != null) sumRent += rent;
    sumMisc  += misc;
    sumCGST  += gst.cgst;
    sumUTGST += gst.utgst;
    sumSGST  += gst.sgst;
    sumTotal += net;

    return { ...row, rent, misc, net, gst };
  });

  return (
    <section className="container-fluid">
      <h3 className="ctitle">Invoice Details</h3>

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label">Guest House</label>
          <select className="form-select" value={guestHouseID} onChange={(e) => setGuestHouseID(e.target.value)}>
            <option value="ALL">All Guest Houses</option>
            {guesthouses.map((gh) => (
              <option key={gh.GuestHouseID} value={String(gh.GuestHouseID)}>{gh.GuestHouse}</option>
            ))}
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">From Date</label>
          <input className="form-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <label className="form-label">To Date</label>
          <input className="form-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="col-md-2">
          <label className="form-label">Show</label>
          <select className="form-select" value={viewType} onChange={(e) => setViewType(e.target.value)}>
            <option value="all">All</option>
            <option value="invoices">Invoices</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="col-md-auto d-flex gap-2 align-items-end">
          <button type="button" className="btn btn-success" onClick={fetchData} disabled={loading}>View</button>
          <button type="button" className="btn btn-success" onClick={handleExport} disabled={loading}>Excel</button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted mb-2">Loading...</div>}

      {!loading && searched && (
        <div style={{ overflow: "visible" }}>
          <table className="table table-bordered table-striped table-hover align-middle" style={{ borderCollapse: "collapse", fontSize: "0.85rem", "--bs-table-border-color": "#000", "--bs-table-striped-bg": "#f9f9f9", "--bs-table-hover-bg": "#aed6f1" }}>
            <thead style={{ "--bs-table-bg": "#008080", color: "white" }}>
              <tr>
                <th style={nw}>SNo</th>
                {showCancelCols && <th style={nw}>Status</th>}
                <th style={nw}>BookID</th>
                <th style={nw}>JobID</th>
                <th style={nw}>InvoiceNo</th>
                <th style={nw}>InvDt</th>
                <th style={nw}>Time</th>
                <th style={transIdCol}>Trans ID</th>
                <th style={nw}>PayMode</th>
                <th style={nw}>CheckIn</th>
                <th style={nw}>CheckOut</th>
                <th style={nw}>Days</th>
                <th style={visitorNameCol}>Visitor Name</th>
                <th style={addressCol}><div style={addressInner}>Address</div></th>
                <th style={nw}>State</th>
                <th style={right}>Rent</th>
                <th style={right}>Misc</th>
                <th style={right}>CGST</th>
                <th style={right}>UTGST</th>
                <th style={right}>SGST</th>
                <th style={right}>Total</th>
                <th style={nw}>Rep Dt</th>
                <th style={nw}>BillNo</th>
                {showCancelCols && <th style={nw}>Cancel Date</th>}
                {showCancelCols && <th style={nw}>Cancelled By</th>}
                <th style={nw}>Invoice</th>
                <th style={nw}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {processed.length === 0 && (
                <tr><td colSpan={showCancelCols ? 27 : 24} className="text-center text-muted">No records found.</td></tr>
              )}
              {processed.map((row, idx) => {
                const isCancelled = row.Status === "Cancelled";
                return (
                <tr key={`${row.InvoiceID}-${row.Status}-${idx}`}>
                  <td style={nw}>{idx + 1}</td>
                  {showCancelCols && (
                    <td style={{ ...nw, color: isCancelled ? "#dc3545" : "#198754", fontWeight: 600 }}>
                      {row.Status || ""}
                    </td>
                  )}
                  <td style={nw}>{row.BookingID || ""}</td>
                  <td style={nw}>{row.JobID || ""}</td>
                  <td style={nw}>{row.InvoiceNumber || ""}</td>
                  <td style={nw}>{fmtDate(row.InvoiceDate, true)}</td>
                  <td style={nw}>{fmtTime(row.InvoiceDate)}</td>
                  <td style={transIdCol} title={row.TransactionID || ""}>{row.TransactionID || ""}</td>
                  <td style={nw}>{PM_LABELS[row.PaymentMode] || row.PaymentMode || ""}</td>
                  <td style={nw}>{fmtDateTime(row.CheckIn)}</td>
                  <td style={nw}>{fmtDateTime(row.CheckOut)}</td>
                  <td style={nw}>{calcDays(row.CheckIn, row.CheckOut)}</td>
                  <td style={visitorNameCol} title={row.VisitorName || ""}>{row.VisitorName || ""}</td>
                  <td style={addressCol}><div style={addressInner} title={row.Address || ""}>{row.Address || ""}</div></td>
                  <td style={nw}>{row.State || ""}</td>
                  <td style={right}>{row.rent != null ? row.rent.toFixed(2) : ""}</td>
                  <td style={right}>{fmtN(row.misc)}</td>
                  <td style={right}>{fmtN(row.gst.cgst)}</td>
                  <td style={right}>{fmtN(row.gst.utgst)}</td>
                  <td style={right}>{fmtN(row.gst.sgst)}</td>
                  <td style={right}>{row.net.toFixed(2)}</td>
                  <td style={nw}>{fmtDate(row.ReceiptDate)}</td>
                  <td style={nw}>{billNo(row)}</td>
                  {showCancelCols && <td style={nw}>{fmtDateTime(row.CancelDate)}</td>}
                  {showCancelCols && <td style={nw}>{row.CancelledByName || ""}</td>}
                  <td style={nw}>
                    {row.BookingID ? (
                      <button type="button" style={{ ...linkBtn, color: isCancelled ? "#dc3545" : "#198754" }}
                        onClick={() => openInvoice(row.BookingID, row.InvoiceID)}>
                        Invoice
                      </button>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td style={nw}>
                    {row.BookingID ? (
                      <button type="button" style={{ ...linkBtn, color: "#997404" }}
                        onClick={() => openEdit(row.BookingID)}>
                        Edit
                      </button>
                    ) : <span className="text-muted">—</span>}
                  </td>
                </tr>
                );
              })}
              {processed.length > 0 && (
                <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                  <td colSpan={showCancelCols ? 15 : 14} style={{ textAlign: "right" }}>Total</td>
                  <td style={right}>{sumRent.toFixed(2)}</td>
                  <td style={right}>{sumMisc.toFixed(2)}</td>
                  <td style={right}>{sumCGST.toFixed(2)}</td>
                  <td style={right}>{sumUTGST.toFixed(2)}</td>
                  <td style={right}>{sumSGST.toFixed(2)}</td>
                  <td style={right}>{sumTotal.toFixed(2)}</td>
                  <td colSpan={showCancelCols ? 6 : 4}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
